const imported_stylus_components = require('.cache/react-style-loader/src/pages/TaskDefinition/components/TaskTree.styl');
import { Button, Callout, Menu, Tag } from '@blueprintjs/core';
import { Tooltip2 } from '@blueprintjs/popover2';
import LinkMenuItem from 'src/components/LinkMenuItem';
import TaskTypeIcon from 'src/pages/Tasks/components/TaskTypeIcon';
import linkTo from 'src/utils/linkTo';
import { useAppContext } from 'src/components/AppContext';
import { type ClassAndStyle } from '@pi/ui';
import { type Maybe } from '@pi/types';
import { useMemo } from 'react';
import { Pipeline } from '@pi/shared-utils';
import _ from 'lodash';
import { type TaskDefinitionWorkflowNodeFragmentFragment } from 'src/generated/graphql';
import { v4 as uuidV4 } from 'uuid';

import useTaskTree, { useTaskWorkflowInfo } from '../hooks/useTaskTree';

import type { TaskTreeNode } from '../hooks/useTaskTree';
import type { Intent } from '@blueprintjs/core';

export interface TaskTreeProps extends ClassAndStyle {
    taskId: string;
}

const TaskTree: React.FC<TaskTreeProps> = ({ taskId, ...rest }) => {
    const { loading, root } = useTaskTree(taskId);
    const { update, pinnedTaskDefinition } = useAppContext();

    const nodes = useMemo(() => (root ? flattenTreeNode(root) : []), [root]);
    const parentNodes = useTaskParents(taskId);

    if (loading) return <div>loading ...</div>;
    if (!root) return null;

    const childrenVisible = nodes.length > 1;
    const parentsVisible = parentNodes.length > 1;

    if (!childrenVisible && !parentsVisible) return null;

    const baseMaxHeight = childrenVisible && parentsVisible ? '25vh' : '50vh';
    const paneStyle = { maxHeight: `calc(${baseMaxHeight} - 2rem)`, overflow: 'auto' };

    const isPinned = pinnedTaskDefinition === taskId;

    return (
        <Root {...rest} icon='layout-hierarchy' title='Task Tree'>
            {nodes.length > 1 && (
                <TaskTreeSection label='Children' nodes={nodes} style={paneStyle} />
            )}
            {!!parentNodes.length && (
                <TaskTreeSection
                    label='Parents and dependencies'
                    nodes={parentNodes}
                    style={paneStyle}
                />
            )}
            <Button
                text={isPinned ? 'Unpin Task' : 'Pin Task'}
                rightIcon={isPinned ? 'unpin' : 'pin'}
                intent='primary'
                small
                minimal
                style={{
                    position: 'absolute',
                    top: '.75rem',
                    right: '.65rem',
                }}
                onClick={() =>
                    update({
                        pinnedTaskDefinition: isPinned ? null : taskId,
                    })
                }
            />
        </Root>
    );
};

const TaskTreeSection: React.FC<{
    label: string;
    nodes: Array<{ node: TaskTreeNode; indent: number }>;
    style?: React.CSSProperties;
}> = ({ label, nodes, style }) => (
    <>
        <div
            style={{
                marginTop: '1rem',
                marginLeft: '-1.5rem',
            }}
        >
            {label}
        </div>
        <Menu style={style}>
            {nodes.map(({ node, indent }) => (
                <LinkMenuItem
                    key={node.id}
                    to={linkTo.tasker.taskDefinition.edit.url({ taskId: node.task._id })}
                    text={`${node.task.name} ${node.count > 1 ? `[${node.count}]` : ''}`}
                    style={{ marginLeft: indent * 1.5 + 'rem' }}
                    icon={<TaskTypeIcon type={node.task.type} enabled={node.task.enabled} />}
                    labelElement={
                        <Tags>
                            <NodeTags node={node} />
                        </Tags>
                    }
                />
            ))}
        </Menu>
    </>
);

const buildAncestorTreeNodes = (
    allTasks: Map<string, TaskDefinitionWorkflowNodeFragmentFragment>,
    task: TaskDefinitionWorkflowNodeFragmentFragment,
    processedTaskDefIds: Set<string> = new Set(),
): Array<{
    node: TaskTreeNode;
    indent: number;
    // task: TaskDefinitionWorkflowNodeFragmentFragment;
}> => {
    if (processedTaskDefIds.has(task._id)) return []; // bail out if there is a cyclic reference
    processedTaskDefIds.add(task._id);

    const result: Array<{
        node: TaskTreeNode;
        indent: number;
    }> = [];

    const parents = Pipeline.from(allTasks.values()).filter(
        v => v.children.some(c => c._id === task._id) && v.enabled,
    );

    for (const parent of parents) {
        const parentAncestors = buildAncestorTreeNodes(allTasks, parent, processedTaskDefIds);

        if (parentAncestors.length) {
            for (const ancestor of parentAncestors) {
                result.push(ancestor);

                if (ancestor.node.task._id === parent._id) {
                    result.push(
                        ...getAncestorTreeDependencyNodes(
                            allTasks,
                            task,
                            parent,
                            ancestor.indent + 1,
                        ),
                    );
                    result.push({
                        node: mapTaskInfoToTreeNode(task, parent),
                        indent: ancestor.indent + 1,
                    });
                }
            }
        } else {
            result.push({
                node: mapTaskInfoToTreeNode(parent),
                indent: 0,
            });
            result.push(...getAncestorTreeDependencyNodes(allTasks, task, parent, 1));
            result.push({
                node: mapTaskInfoToTreeNode(task, parent),
                indent: 1,
            });
        }
    }

    return result;
};

const getAncestorTreeDependencyNodes = (
    allTasks: Map<string, TaskDefinitionWorkflowNodeFragmentFragment>,
    task: TaskDefinitionWorkflowNodeFragmentFragment,
    parent: TaskDefinitionWorkflowNodeFragmentFragment,
    indent: number,
    processedTaskDefIds: Set<string> = new Set(),
): Array<{
    node: TaskTreeNode;
    indent: number;
}> => {
    if (processedTaskDefIds.has(task._id)) return [];
    processedTaskDefIds.add(task._id);

    const result: Array<{
        node: TaskTreeNode;
        indent: number;
    }> = [];

    const child = parent.children.find(c => c._id === task._id);
    const dependencyGroup = child?.taskDependencies;
    if (dependencyGroup) {
        for (const c of parent.children) {
            if (c.taskGroup !== dependencyGroup) continue;

            const dependencyTask = allTasks.get(c._id);
            if (!dependencyTask) continue;

            result.push(
                ...getAncestorTreeDependencyNodes(
                    allTasks,
                    dependencyTask,
                    parent,
                    indent,
                    processedTaskDefIds,
                ),
            );

            result.push({
                node: mapTaskInfoToTreeNode(dependencyTask, parent),
                indent,
            });
        }
    }
    return result;
};

const mapTaskInfoToTreeNode = (
    task: TaskDefinitionWorkflowNodeFragmentFragment,
    parent?: TaskDefinitionWorkflowNodeFragmentFragment,
): TaskTreeNode => {
    const childNode = parent?.children?.find(c => c._id === task._id);

    return {
        id: uuidV4(),
        task: _.pick(task, ['_id', 'name', 'type', 'enabled']),

        children: [],
        count: childNode?.count ?? 1,
        many: childNode?.many,
        optional: childNode?.optional,

        dependencies: [],
        dependencyDepth: 0,
        dependentIndex: 0,
    };
};

const useTaskParents = (taskId: Maybe<string>): Array<{ node: TaskTreeNode; indent: number }> => {
    const { taskWorkflowNodes } = useTaskWorkflowInfo();

    return useMemo(() => {
        if (!taskWorkflowNodes || !taskId) return [];

        const task = taskWorkflowNodes.get(taskId);
        if (!task) return [];

        return buildAncestorTreeNodes(taskWorkflowNodes, task);
    }, [taskWorkflowNodes, taskId]);
};

export default TaskTree;

function flattenTreeNode(root: TaskTreeNode) {
    const result: Array<{ indent: number; node: TaskTreeNode }> = [];

    const iterate = (node: TaskTreeNode, indent: number) => {
        result.push({
            node,
            indent,
        });

        node.children.forEach(child => iterate(child, indent + 1));
    };

    iterate(root, 0);

    return result;
}

export const tagRenderers = {
    many: {
        intent: 'none',
        minimal: true,
        check: node => !!node.many,
        tooltip:
            'This task type is created inside a loop (such as "Each") and can have any number of instances generated based on the underlying input',
    },
    optional: {
        intent: 'warning',
        check: node => !!node.optional,
        tooltip:
            'This task type is created inside a conditional statement (such as "If" or "Filter"). This means it\'s not a guarantee it will be created, dependening on the underlying input',
    },
    disabled: {
        intent: 'none',
        check: node => !node.task.enabled,
        tooltip: (
            <>
                This task has been disabled
                <br />
                <br />
                Go to the task definition page and switch it's "Enabled" toggle to allow tasks of
                this kind to be processed
            </>
        ),
    },
    cyclic: {
        intent: 'danger',
        check: node => !!node.cyclic,
        tooltip: (
            <>
                This task type eventually creates another copy of itself, so it can potentially
                cycle indefinitely
                <br />
                <br />
                It is strongly encouraged to avoid having cycles in your task graph
            </>
        ),
    },
} satisfies Record<
    string,
    {
        tooltip: React.ReactNode;
        minimal?: boolean;
        intent: Intent;
        check: (node: TaskTreeNode) => boolean;
    }
>;

export const NodeTags: React.FC<{
    node: TaskTreeNode;
    exclude?: Array<keyof typeof tagRenderers>;
    include?: Array<keyof typeof tagRenderers>;
}> = ({ node, include, exclude = [] }) => {
    return (
        <>
            {Object.tsKeys(tagRenderers).map(key => {
                const { check, intent, tooltip, minimal } = tagRenderers[key] as any;

                if ((include && !include.includes(key)) || exclude.includes(key) || !check(node))
                    return null;

                return (
                    <Tooltip2 content={tooltip} key={key}>
                        <Tag intent={intent} minimal={minimal}>
                            {key}
                        </Tag>
                    </Tooltip2>
                );
            })}
        </>
    );
};

const Root = imported_stylus_components.Root.withComponent(Callout);
const Tags = imported_stylus_components.Tags;